import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.76, 0, 0.24, 1];

const Line = ({ children, delay }) => (
  <motion.span
    className="block overflow-hidden pb-[0.08em]"
    initial={{ opacity: 0, y: 60 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 1, ease: EASE, delay }}
  >
    {children}
  </motion.span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="home" data-testid="hero-section" className="relative h-screen w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src="/school-cover.jpg"
          alt="Bhakti Svarup Vidya Niketan campus"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/80 to-black/45" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/35 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-16 lg:pb-20">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="flex items-center gap-3 mb-6 text-saffron uppercase tracking-[0.3em] text-xs sm:text-sm"
        >
          <span className="h-px w-10 bg-saffron" />
          Tradition · Modernity · Character
        </motion.p>

        <h1 className="ff-display text-cream text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight max-w-5xl">
          <Line delay={0.45}>Where ancient</Line>
          <Line delay={0.6}>
            <span className="italic text-saffron">wisdom</span> meets
          </Line>
          <Line delay={0.75}>tomorrow's world.</Line>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-8 flex flex-col sm:flex-row sm:items-center gap-5"
        >
          <p className="text-cream/85 max-w-md text-base sm:text-lg font-light">
            Bhakti Svarup Vidya Niketan — a school for the whole child, from Primary to Secondary.
          </p>
          <a
            href="#admissions"
            data-testid="hero-cta"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-cream text-ink hover:bg-saffron transition-colors duration-300 w-fit"
          >
            Begin Admission →
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-6 lg:right-10 z-10 text-cream/70 text-xs tracking-[0.25em] uppercase [writing-mode:vertical-rl]"
      >
        Scroll to explore
      </motion.div>
    </section>
  );
};
